import React, { useState, useEffect, useRef, useCallback } from "react";
import { db } from "../utility/firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  getDocs,
  getDoc,
  startAfter,
  serverTimestamp,
  doc,
  writeBatch,
} from "firebase/firestore";

import Message from "../components/Message";
import BeatLoader from "react-spinners/BeatLoader";
import generateItems from "../utility/groupbydate";

const delay = (delay) => {
  return new Promise((res, rej) => {
    setTimeout(() => {
      res("finish");
    }, delay);
  });
};

const MessageList = (props) => {
  const { currentUser, chatUserData, currentChatRoomId, setIsOpen, itemId, suggestedKeywords, setSuggestedKeywords } =
    props;

  const chatMessagePageSize = 20;
  const chatMessageInitialSize = 20;
  const chatMessageFetchMoreSize = 20;

  /* message info */
  const [messageIsInitialLoading, setMessageIsInitialLoading] = useState(true);
  const [messages, setMesssages] = useState([]);
  const [finalMessages, setFinalMessages] = useState([]);
  const [messageIsLoadingMore, setMessageIsLoadingMore] = useState(false);
  const [messagePageNum, setMessagePageNum] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const scrollToBottomRef = useRef("");
  const scrollableDivRef = useRef(null);

  const observer = useRef();
  const lastElementRef = useCallback((node) => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        setMessagePageNum((page) => page + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, []);

  useEffect(() => {
    if (itemId) {
      setMessageIsInitialLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    if (messages.length > 0) {
      setFinalMessages(generateItems(messages));
      //loop through messages to see if .text contains any of the suggestedKeywords, if so, remove it from the suggestedKeywords
      let newSuggestedKeywords = [...suggestedKeywords];
      messages.forEach((message) => {
        newSuggestedKeywords = newSuggestedKeywords.filter((keyword) => {
          return !message.text.toLowerCase().includes(keyword.toLowerCase());
        });
      });
      if (newSuggestedKeywords.length !== suggestedKeywords.length) {
        setSuggestedKeywords(newSuggestedKeywords);
      }
    } else {
      setFinalMessages([]); /* when the lenth of messages is zero, we clear the finalMessages */
    }
  }, [messages, suggestedKeywords]);

  /* get chat messages by page */
  useEffect(() => {
    let fixScrollTimer = null;
    if (!messageIsLoadingMore && !messageIsInitialLoading) {
      setMessageIsLoadingMore(true);

      if (messages.length > 0 && hasMore) {
        const lastMessageCreatedTime =
          messages[messages.length - 1].createdTime;
        const chatRoomRef = collection(
          db,
          "chatRooms",
          `${currentChatRoomId}`,
          "messages"
        );
        const q = query(
          chatRoomRef,
          orderBy("createdTime", "desc"),
          startAfter(lastMessageCreatedTime),
          limit(chatMessagePageSize)
        );

        getDocs(q)
          .then((documentSnapshots) => {
            const messages2 = [];
            documentSnapshots.forEach((doc) => {
              let newData = {
                id: doc.id,
                ...doc.data({ serverTimestamps: "estimate" }),
              };
              messages2.push(newData);
            });

            if (messages2.length < chatMessageFetchMoreSize) {
              setHasMore(false);
            }

            delay(300).then(() => {
              setMesssages((prevState) => {
                return [...prevState, ...messages2];
              });
              setMessageIsLoadingMore(false);  
            });
            fixScrollTimer = delay(300).then(() => {
              fixScrollIssue(1);
            });
          })
          .catch((error) => {});
      }
    }
    return () => {
      setMessageIsLoadingMore(false);
      clearTimeout(fixScrollTimer);
    };
  }, [messagePageNum]);

  /* we have currentChatRoomId, we get messages */
  useEffect(() => {
    if (currentChatRoomId != undefined) {
      setMessageIsInitialLoading(true);

      /* subscribe to message list */
      const unSubMessages = onSnapshot(
        query(
          collection(db, "chatRooms", currentChatRoomId, "messages"),
          orderBy("createdTime", "desc"),
          limit(chatMessageInitialSize)
        ),
        (querySnapshot) => {
          let added = false;
          let updateReadCount = 0;
          const batch = writeBatch(db);
          const messagesFirestore = [];
          querySnapshot.docChanges().forEach((change) => {
            const currentDoc = change.doc.data({
              serverTimestamps: "estimate",
            });
            if (change.type == "added") {
              added = true;
              let newData = {
                id: change.doc.id,
                ...change.doc.data({ serverTimestamps: "estimate" }),
                pending: change.doc.metadata.hasPendingWrites ? true : false,
                sent: change.doc.metadata.hasPendingWrites ? false : true,
              };
              messagesFirestore.push(newData);
            } else if (change.type == "modified") {
              let newData = {
                id: change.doc.id,
                ...change.doc.data({ serverTimestamps: "estimate" }),
                pending: change.doc.metadata.hasPendingWrites ? true : false,
                sent: change.doc.metadata.hasPendingWrites ? false : true,
              };
              setMesssages((prevState) => {
                const modifiedMessages = [];
                prevState.forEach((data, index) => {
                  if (data.id == newData.id) {
                    modifiedMessages.push({
                      ...data,
                      ...newData,
                    });
                  } else {
                    modifiedMessages.push(data);
                  }
                });
                return [...modifiedMessages];
              });
            }

            /* also update the isRead to true */
            const docRef = doc(
              db,
              "chatRooms",
              currentChatRoomId,
              "messages",
              change.doc.id
            );
            if (
              currentDoc.isRead == false &&
              currentDoc.createdUser != currentUser.uid
            ) {
              updateReadCount++;
              batch.update(docRef, {
                isRead: true,
                readTime: serverTimestamp(),
              });
            }
          });

          //Commit the batch
          batch
            .commit()
            .then(() => {
              if (added == true) {
                /* update lastMesage unread counter to 0 */
                getDoc(doc(db, "chatRooms", currentChatRoomId)).then(
                  (chatRoomSnap) => {
                    const chatRoomSnapData = chatRoomSnap.data({
                      serverTimestamps: "estimate",
                    });
                    if (chatRoomSnapData) {
                      if (
                        chatRoomSnapData.lastMessage.sentTo ==
                        currentUser.uid.toString()
                      ) {
                        updateDoc(doc(db, "chatRooms", currentChatRoomId), {
                          "lastMessage.unReadCount": 0,
                        })
                          .then((res) => {})
                          .catch((err) => {});
                      }
                    }
                  }
                );
              }
            })
            .catch((error) => {});

          setMessageIsInitialLoading(false);

          if (messagesFirestore.length >= chatMessagePageSize) setHasMore(true);

          setMesssages((prevState) => {
            return [...messagesFirestore, ...prevState];
          });

          if (added) {
            scrollToBottom();
          }
        }
      );

      return () => {
        setMesssages([]);
        setMessagePageNum(0);
        setHasMore(false);
        unSubMessages();
      };
    }
  }, [currentChatRoomId]);

  /* help function */
  const fixScrollIssue = (pixel = 1) => {
    // const myElement = document.getElementById("scrollableDiv");
    // myElement.scrollTop += pixel;
    // myElement.scrollTop -= pixel;
    scrollableDivRef.current.scrollBy({
      top: -1,
    });
    scrollableDivRef.current.scrollBy({
      top: 1,
    });
  };

  /* helper to trigger to last */
  const scrollToBottom = () => {
    fixScrollIssue();
    delay(200).then(() => {
      scrollToBottomRef.current.scrollIntoView({ behaviour: "smooth" });
    });
  };

  return (
    <div className="messageList" id="scrollableDiv" ref={scrollableDivRef}>
      <div className="messageLoading">
        <BeatLoader color="#333" loading={messageIsInitialLoading} size={13} />
      </div>

      <div ref={scrollToBottomRef}></div>

      {finalMessages.map((data, index) => {
        return (
          <Message
            msg={data}
            createdTimeDate={
              data.type == "day" ? null : data.createdTime.toDate().toString()
            }
            createdDateSeconds={
              data.type == "day" ? null : data.createdTime.seconds
            }
            curretUserId={currentUser.uid}
            chatUserData={chatUserData}
            index={index}
            key={index}
            setIsOpen={setIsOpen}
            msgSpamDefinition={props.msgSpamDefinition}
          />
        );
      })}

      <div className="messageLoadingTop" ref={lastElementRef}>
        {hasMore ? (
          <BeatLoader color="#333" loading={true} css={{}} size={13} />
        ) : null}
      </div>
    </div>
  );
};

export default MessageList;
